/**
 * The words and counts of the batch Export and Run workflow dialogs (1.2.0), kept apart from the dialogs so they can
 * be unit tested: the preflight's tally, the summary after a start ("Started 18 · skipped 2"), and the preflight query.
 */
import { BULK_ACTION_LIMIT, type BatchPreflightResult, type BatchResult, type BatchSkipped, type ExportFormatId, type MasterChoice } from '@prism/contracts';

export interface PreflightTally {
  /** Will start as chosen. */
  ready: number;
  /** Only an archived original stops them: they start with Restore archived originals first. */
  restorable: number;
  /** Will be skipped (the reasons are on each item). */
  blocked: number;
}

export function preflightTally(items: BatchPreflightResult['items']): PreflightTally {
  const t: PreflightTally = { ready: 0, restorable: 0, blocked: 0 };
  for (const i of items) {
    if (i.ok) t.ready++;
    else if (i.restorable) t.restorable++;
    else t.blocked++;
  }
  return t;
}

/** How many a start would act on: the ready ones, and with `restore` the restorable ones too. */
export const startableCount = (t: PreflightTally, restore: boolean) => t.ready + (restore ? t.restorable : 0);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "Will export 18 · 2 skipped" before a start. */
export function tallyText(t: PreflightTally, verb: string, restore: boolean): string {
  const n = startableCount(t, restore);
  const skipped = t.blocked + (restore ? 0 : t.restorable);
  return [`${verb} ${n}`, skipped ? `${skipped} skipped` : null, t.restorable && !restore ? `${plural(t.restorable, 'archived original')} can be restored first` : null]
    .filter(Boolean).join(' · ');
}

/** "Started 18 · skipped 2", with "(1 over the limit)" when the person reached the limit in progress meanwhile. */
export function batchSummaryText(r: BatchResult): string {
  const parts = [`Started ${r.started.length}`];
  if (r.skipped.length) parts.push(`skipped ${r.skipped.length}${r.overLimit ? ` (${r.overLimit} over the limit of ${BULK_ACTION_LIMIT} in progress)` : ''}`);
  return parts.join(' · ');
}

/** The skipped assets grouped by reason, the most common first: [{ message, items }]. */
export function skippedByReason(skipped: BatchSkipped[]): Array<{ code: string; message: string; items: BatchSkipped[] }> {
  const groups = new Map<string, { code: string; message: string; items: BatchSkipped[] }>();
  for (const s of skipped) {
    // Messages that name the asset (“Title”: …) are per asset; the code groups them.
    const key = s.code === 'TOO_MANY_RUNNING' || s.code === 'NOT_FOUND' ? s.code : `${s.code}:${s.message}`;
    const g = groups.get(key) ?? { code: s.code, message: s.message, items: [] };
    g.items.push(s);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) => b.items.length - a.items.length);
}

/** The batch export preflight's query (GET /api/v1/preflight with assetIds). */
export function exportPreflightParams(p: {
  assetIds: string[]; preset: ExportFormatId; quality: 'original' | 'proxy'; range: 'full' | 'programme'; locationId: string | null; master: MasterChoice | null;
}): Record<string, string | null> | null {
  if (!p.assetIds.length || p.assetIds.length > BULK_ACTION_LIMIT) return null;
  return {
    kind: 'asset-export', assetIds: p.assetIds.join(','), preset: p.preset, quality: p.preset === 'original' ? 'original' : p.quality, range: p.range,
    locationId: p.locationId, master: p.master ? JSON.stringify(p.master) : null,
  };
}

/** The batch run preflight's query for one workflow. */
export function runPreflightParams(workflowId: string | null, assetIds: string[]): Record<string, string> | null {
  if (!workflowId || !assetIds.length || assetIds.length > BULK_ACTION_LIMIT) return null;
  return { kind: 'workflow', workflowId, assetIds: assetIds.join(',') };
}
